/* 표시하고 고치기 — 사진 위에 펜으로 그리거나 번호 핀을 꽂고, 번호마다 요청을 적어 AI로 고친다.
   · 보낼 때는 원본 해상도 사진 위에 빨간 표시(선·번호 핀)를 그린 한 장을 만들어 보낸다(위치가 정확하고 표시도 깨끗이 지웠다 — 실측)
   · 좌표는 사진 기준 0~1로 저장해서 화면 크기와 상관없이 같은 자리에 그린다 */
import { IcAi, IcClose, IcUndo } from './icons';
import { Badge, Button, Divider, Input, Modal, Segmented, Skeleton, Textarea } from '@indipia/ui-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { submitMarkup } from '../ai/agent';
import type { Mark } from '../ai/prompts';
import { loadImage } from '../model/images';
import type { El } from '../model/types';
import { toast } from '../store/editor';
import { cx, dialogBox, groupTitle, hint, iconBtn, vDivider } from './ui';

const MARK_COLOR = '#FF2D55'; // dq-ignore: 서버로 보내는 사진에 그리는 표시 색 — 화면의 핀·번호도 이 색(사진 위 표시와 같아야 함)
const MARK_BG = { background: MARK_COLOR, borderColor: MARK_COLOR };

function badge(g: CanvasRenderingContext2D, x: number, y: number, n: number, r: number) {
  g.fillStyle = MARK_COLOR; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  g.lineWidth = Math.max(2, r / 6); g.strokeStyle = '#fff'; g.stroke(); // dq-ignore: 사진에 그리는 핀 테두리
  g.fillStyle = '#fff'; g.font = `bold ${Math.round(r * 1.15)}px sans-serif`; g.textAlign = 'center'; g.textBaseline = 'middle'; // dq-ignore: 핀 번호
  g.fillText(String(n), x, y + r * 0.05);
}
/** 표시 그리기 — 화면(미리보기)과 보낼 사진 모두 같은 함수로 */
function drawMarks(g: CanvasRenderingContext2D, W: number, H: number, marks: Mark[], pensOnly: boolean) {
  const lw = Math.max(3, W / 160), pr = Math.max(11, W / 45);
  marks.forEach((mk, i) => {
    if (mk.type === 'pen' && mk.pts?.length) {
      g.strokeStyle = MARK_COLOR; g.lineWidth = lw; g.lineCap = 'round'; g.lineJoin = 'round';
      g.beginPath();
      mk.pts.forEach(([x, y], k) => (k ? g.lineTo(x * W, y * H) : g.moveTo(x * W, y * H)));
      g.stroke();
      const [sx, sy] = mk.pts[0];
      badge(g, sx * W, sy * H, i + 1, pr * 0.8);
    } else if (mk.type === 'pin' && !pensOnly) badge(g, (mk.x || 0) * W, (mk.y || 0) * H, i + 1, pr);
  });
}

export function MarkupModal({ el, onClose }: { el: El; onClose: () => void }) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [tool, setTool] = useState<'pin' | 'pen'>('pin');
  const [marks, setMarks] = useState<Mark[]>([]);
  const [how, setHow] = useState('');
  const imgRef = useRef<HTMLImageElement>(null);
  const cvRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef<Mark | null>(null);

  useEffect(() => {
    let alive = true;
    loadImage(el.src || '', false).then((im) => alive && setImg(im)).catch(() => { toast('사진을 불러오지 못했어요'); onClose(); });
    return () => { alive = false; };
  }, [el.src, onClose]);

  const redraw = useCallback(() => {
    const im = imgRef.current, cv = cvRef.current;
    if (!im || !cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = im.clientWidth * dpr; cv.height = im.clientHeight * dpr;
    cv.style.width = im.clientWidth + 'px'; cv.style.height = im.clientHeight + 'px';
    const g = cv.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, cv.width, cv.height);
    drawMarks(g, cv.width, cv.height, marks, true); // 핀은 화면에서 DOM으로 그려야 누를 수 있으니 편집 화면엔 펜만
  }, [marks]);
  useEffect(() => { redraw(); }, [redraw, img]);
  useEffect(() => { window.addEventListener('resize', redraw); return () => window.removeEventListener('resize', redraw); }, [redraw]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const typing = /INPUT|TEXTAREA/.test((document.activeElement as HTMLElement)?.tagName || '');
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !typing) { e.preventDefault(); e.stopImmediatePropagation(); setMarks((m) => m.slice(0, -1)); }
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [onClose]);

  const point = (e: { clientX: number; clientY: number }) => {
    const r = imgRef.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const focusLast = (n: number) => setTimeout(() => document.querySelector<HTMLInputElement>(`[data-showcase] [data-note="${n}"]`)?.focus(), 0);
  const onDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-pin]') || !imgRef.current) return;
    e.preventDefault();
    const [x, y] = point(e);
    if (tool === 'pin') { setMarks((m) => { focusLast(m.length); return [...m, { type: 'pin', x, y, note: '' }]; }); return; }
    const d: Mark = { type: 'pen', pts: [[x, y]], note: '' };
    drawing.current = d;
    setMarks((m) => [...m, d]);
    const move = (ev: MouseEvent) => {
      d.pts!.push(point(ev));
      const cv = cvRef.current, g = cv?.getContext('2d');
      if (cv && g) { g.clearRect(0, 0, cv.width, cv.height); drawMarks(g, cv.width, cv.height, markRef.current, true); }
    };
    const up = () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      drawing.current = null;
      setMarks((m) => { if (d.pts!.length < 3) return m.filter((x) => x !== d); focusLast(m.length - 1); return [...m]; }); // 살짝 누르기만 한 건 버린다
    };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };
  const markRef = useRef(marks);
  markRef.current = marks;

  const canGo = marks.length > 0 && (!!how.trim() || marks.some((m) => (m.note || '').trim()));
  const go = () => {
    if (!img || !canGo) return;
    // 원본 해상도 사진 + 빨간 표시 한 장
    const W = img.naturalWidth, H = img.naturalHeight;
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const g = c.getContext('2d')!;
    g.drawImage(img, 0, 0, W, H);
    drawMarks(g, W, H, marks, false);
    const marked = c.toDataURL('image/jpeg', 0.92);
    onClose();
    void submitMarkup(el, marked, marks.map((m) => ({ ...m })), how.trim());
  };
  return (
    <Modal open size="xl" className={cx(dialogBox, 'max-w-page-xl')} title="표시하고 고치기" onClose={onClose}
      footer={<>
        <Button variant="etc" onClick={onClose}>취소</Button>
        <Button disabled={!canGo} onClick={go}><IcAi size={16} />AI로 고치기</Button>
      </>}
    >
      <p className="m-0 border-b border-border px-05 py-03 text-body-sm text-text-secondary">고칠 곳에 핀을 꽂거나 펜으로 그리고, 번호마다 무엇을 할지 적어 주세요</p>
      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col bg-surface">
          <div className="flex items-center gap-sm border-b border-border bg-surface-raised px-04 py-03" role="toolbar" aria-label="표시 도구">
            <Segmented size="sm" options={[{ value: 'pin', label: '핀 꽂기' }, { value: 'pen', label: '펜으로 그리기' }]} value={tool} onChange={(v) => setTool(v as 'pin' | 'pen')} aria-label="표시 방법" />
            <Divider className={vDivider} role="separator" aria-orientation="vertical" />
            <Button size="sm" variant="etc" title="마지막 표시 지우기 (Ctrl+Z)" disabled={!marks.length} onClick={() => setMarks((m) => m.slice(0, -1))}><IcUndo size={16} />되돌리기</Button>
            <Button size="sm" variant="etc" title="표시 모두 지우기" disabled={!marks.length} onClick={() => setMarks([])}>모두 지우기</Button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-05">
            {!img && <Skeleton variant="block" className="size-full" />}
            <div className="relative cursor-crosshair select-none leading-none" onMouseDown={onDown}>
              {img && <img ref={imgRef} className="pointer-events-none block max-h-[calc(100vh-var(--spacing-13)-var(--spacing-12))] max-w-full rounded-sm shadow-[var(--elevation-2)]" src={img.src} alt="고칠 사진" onLoad={redraw} />}
              <canvas ref={cvRef} className="pointer-events-none absolute left-0 top-0" aria-hidden />
              <div className="pointer-events-none absolute inset-0">
                {marks.map((m, i) => m.type === 'pin' && (
                  <b key={i} data-pin className="pointer-events-auto absolute flex size-07 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-thick border-solid !border-[var(--gray-0)] text-body-sm leading-none text-[var(--gray-0)] shadow-[var(--elevation-1)]" style={{ background: MARK_COLOR, left: `${(m.x || 0) * 100}%`, top: `${(m.y || 0) * 100}%` }} title={`${i + 1}번`}
                    onClick={() => document.querySelector<HTMLInputElement>(`[data-showcase] [data-note="${i}"]`)?.focus()}>{i + 1}</b>
                ))}
              </div>
            </div>
          </div>
        </div>
        <aside className="flex w-side-panel shrink-0 flex-col gap-sm overflow-y-auto border-l border-border p-05">
          <h3 className={cx(groupTitle, 'm-0')}>표시별 요청</h3>
          <div className="flex flex-col gap-sm">
            {!marks.length && <p className={hint}>사진을 눌러 핀을 꽂거나, 펜으로 고칠 곳을 그려 보세요.</p>}
            {marks.map((m, i) => (
              <div key={i} className="flex items-center gap-sm">
                <Badge className="shrink-0 text-[var(--gray-0)]" style={MARK_BG}>{i + 1}</Badge>
                <Input size="sm" width="fill" data-note={i} aria-label={`${i + 1}번 표시 요청`} value={m.note} placeholder={m.type === 'pin' ? '예: 여기에 키 큰 화분' : '예: 이 벽에 액자 두 개'}
                  onChange={(e) => setMarks((all) => all.map((x, k) => (k === i ? { ...x, note: e.target.value } : x)))} />
                <Button size="sm" variant="ghost" className={iconBtn} aria-label={`${i + 1}번 표시 지우기`} title="이 표시 지우기" onClick={() => setMarks((all) => all.filter((_, k) => k !== i))}><IcClose size={16} /></Button>
              </div>
            ))}
          </div>
          <Textarea label="전체 요청 (선택)" textareaClassName="min-h-11 resize-y" value={how} onChange={(e) => setHow(e.target.value)} placeholder="예: 오가닉 스타일로, 밝은 우드 톤 위주로" />
          <p className={hint}>결과 사진에서는 빨간 표시를 지우고, 표시한 곳만 고쳐요. 마음에 안 들면 "원복시켜줘"나 ↩ 되돌리기로 돌아가요.</p>
        </aside>
      </div>
    </Modal>
  );
}
